import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AppAutocomplete, AppButton, AppCheckbox, AppTextField } from "../../components/form";
import LlmProviderAutocomplete from "../../components/LlmProviderAutocomplete";
import { AuthPageShell } from "../../components/layout";
import { workspaceService } from "../../services/workspaceService";
import type {
  BeasLlmModelOption,
  LlmMode,
  OpenSearchMode,
  WorkspaceSettings,
} from "../../services/workspaceService";
import { clearSetupDeferred, deferSetup } from "../../utils/setupGate";

export default function SetupWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);
  const [llmApiKey, setLlmApiKey] = useState("");
  const [osPassword, setOsPassword] = useState("");
  const [llmMode, setLlmMode] = useState<LlmMode>("BEAS_MANAGED");
  const [beasLlmModels, setBeasLlmModels] = useState<BeasLlmModelOption[]>([]);
  const [beasLlmModelId, setBeasLlmModelId] = useState("");
  const [osMode, setOsMode] = useState<OpenSearchMode>("BEAS_MANAGED");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const steps = [
    t("workspace.setup.steps.llm"),
    t("workspace.setup.steps.opensearch"),
    t("workspace.setup.steps.finish"),
  ];

  const load = async () => {
    const s = await workspaceService.getSettings();
    setSettings(s);
    if (s.openSearchMode === "EXTERNAL" || s.openSearchMode === "BEAS_MANAGED") {
      setOsMode(s.openSearchMode);
    }
    if (s.llmMode === "EXTERNAL" || s.llmMode === "BEAS_MANAGED") {
      setLlmMode(s.llmMode);
    }
    setBeasLlmModelId(s.beasLlmModelId || "");
    if (s.setupComplete) {
      clearSetupDeferred();
      navigate("/report");
    }
  };

  useEffect(() => {
    load().catch((e) => setError(e.message || t("workspace.setup.loadError")));
    workspaceService
      .listBeasLlmModels()
      .then((rows) => setBeasLlmModels(Array.isArray(rows) ? rows : []))
      .catch(() => setBeasLlmModels([]));
  }, []);

  const skipForLater = () => {
    deferSetup();
    navigate("/report");
  };

  const savePartial = async (payload: Record<string, unknown>) => {
    setSaving(true);
    setError(null);
    try {
      const s = await workspaceService.saveSettings(payload);
      setSettings(s);
      setInfo(t("workspace.setup.saved"));
    } catch (e: any) {
      setError(e.message || t("workspace.setup.saveError"));
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const nextFromLlm = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      if (llmMode === "BEAS_MANAGED") {
        if (!beasLlmModelId) {
          throw new Error(t("workspace.setup.beasLlmPickRequired"));
        }
        const s = await workspaceService.selectBeasLlm(beasLlmModelId);
        setSettings(s);
        setInfo(t("workspace.setup.saved"));
      } else {
        let baseUrl = settings.llmBaseUrl?.trim() || "";
        if (settings.llmProvider === "OPENAI" && baseUrl && !/^https?:\/\//i.test(baseUrl)) {
          baseUrl = "";
        }
        await savePartial({
          llmMode: "EXTERNAL",
          llmProvider: settings.llmProvider,
          llmBaseUrl: baseUrl,
          chatModel: settings.chatModel,
          embeddingModel: settings.embeddingModel,
          ...(llmApiKey ? { llmApiKey } : {}),
        });
        setSettings((prev) => (prev ? { ...prev, llmBaseUrl: baseUrl || undefined } : prev));
      }
      setActive(1);
    } catch (e: any) {
      setError(e.message || t("workspace.setup.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const nextFromOs = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      if (osMode === "BEAS_MANAGED") {
        const s = await workspaceService.provisionBeasOpenSearch();
        setSettings(s);
        setInfo(t("workspace.setup.beasProvisioned"));
      } else {
        const s = await workspaceService.saveSettings({
          openSearchMode: "EXTERNAL",
          openSearchUrl: settings.openSearchUrl,
          openSearchUsername: settings.openSearchUsername,
          openSearchSslInsecure: settings.openSearchSslInsecure,
          ...(osPassword ? { openSearchPassword: osPassword } : {}),
        });
        setSettings(s);
        setInfo(t("workspace.setup.saved"));
      }
      setActive(2);
    } catch (e: any) {
      setError(e.message || t("workspace.setup.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const runTests = async () => {
    setError(null);
    setInfo(null);
    setSaving(true);
    try {
      await workspaceService.testLlm();
      if (settings?.openSearchMode !== "BEAS_MANAGED") {
        await workspaceService.testOpenSearch();
      }
      const status = await workspaceService.setupStatus();
      if (status.setupComplete) {
        clearSetupDeferred();
        setInfo(t("workspace.setup.complete"));
        navigate("/report");
      } else {
        setError(t("workspace.setup.testsFailed"));
        await load();
      }
    } catch (e: any) {
      setError(e.message || t("workspace.setup.testError"));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <AuthPageShell
        maxWidth={720}
        title={t("workspace.setup.title")}
        subtitle={t("workspace.setup.subtitle")}
      >
        {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : t("workspace.setup.loading")}
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell
      maxWidth={720}
      title={t("workspace.setup.title")}
      subtitle={t("workspace.setup.subtitle")}
    >
      <Alert severity="info" sx={{ mb: 1.5 }}>
        {t("workspace.setup.deferHint")}
      </Alert>
      <Stepper activeStep={active}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          {info}
        </Alert>
      )}

      {active === 0 && (
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("workspace.setup.llmModeTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("workspace.setup.llmModeHint")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                borderColor: llmMode === "BEAS_MANAGED" ? "primary.main" : "divider",
                borderWidth: llmMode === "BEAS_MANAGED" ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => setLlmMode("BEAS_MANAGED")}>
                <CardContent>
                  <Typography fontWeight={700}>{t("workspace.setup.beasLlmTitle")}</Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {t("workspace.setup.beasLlmDescription")}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                borderColor: llmMode === "EXTERNAL" ? "primary.main" : "divider",
                borderWidth: llmMode === "EXTERNAL" ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => setLlmMode("EXTERNAL")}>
                <CardContent>
                  <Typography fontWeight={700}>{t("workspace.setup.ownLlmTitle")}</Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {t("workspace.setup.ownLlmDescription")}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Stack>

          {llmMode === "BEAS_MANAGED" ? (
            <AppAutocomplete
              label={t("workspace.setup.beasLlmModel")}
              options={beasLlmModels}
              value={beasLlmModels.find((m) => m.id === beasLlmModelId) || null}
              onChange={(_, v) => setBeasLlmModelId(v?.id || "")}
              getOptionLabel={(o) => (o ? o.name : "")}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              helperText={t("workspace.setup.beasLlmHelp")}
            />
          ) : (
            <>
              <LlmProviderAutocomplete
                value={settings.llmProvider}
                onChange={(llmProvider) => setSettings({ ...settings, llmProvider })}
              />
              <AppTextField
                label={t("workspace.setup.llmBaseUrl")}
                placeholder={
                  settings.llmProvider === "OLLAMA"
                    ? "http://127.0.0.1:11434"
                    : settings.llmProvider === "OPENAI"
                      ? "https://api.openai.com (leave blank for default)"
                      : "https://your-gateway.example.com"
                }
                value={settings.llmBaseUrl || ""}
                onChange={(e) => setSettings({ ...settings, llmBaseUrl: e.target.value })}
                helperText={t("workspace.setup.llmBaseUrlHelp")}
              />
              <AppTextField
                label={
                  settings.llmApiKeySet
                    ? t("workspace.setup.llmApiKeyKeep")
                    : t("workspace.setup.llmApiKey")
                }
                type="password"
                value={llmApiKey}
                onChange={(e) => setLlmApiKey(e.target.value)}
              />
              <AppTextField
                label={t("workspace.setup.chatModel")}
                value={settings.chatModel || ""}
                onChange={(e) => setSettings({ ...settings, chatModel: e.target.value })}
              />
              <AppTextField
                label={t("workspace.setup.embeddingModel")}
                value={settings.embeddingModel || ""}
                onChange={(e) => setSettings({ ...settings, embeddingModel: e.target.value })}
              />
            </>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
            <AppButton variant="contained" disabled={saving} onClick={() => nextFromLlm().catch(() => {})}>
              {t("workspace.setup.continue")}
            </AppButton>
            <AppButton variant="outlined" onClick={skipForLater}>
              {t("workspace.setup.configureLater")}
            </AppButton>
          </Box>
        </Stack>
      )}

      {active === 1 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            {t("workspace.setup.osModeTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t("workspace.setup.osModeHint")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                borderColor: osMode === "BEAS_MANAGED" ? "primary.main" : "divider",
                borderWidth: osMode === "BEAS_MANAGED" ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => setOsMode("BEAS_MANAGED")}>
                <CardContent>
                  <Typography fontWeight={700}>{t("workspace.setup.beasTitle")}</Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {t("workspace.setup.beasDescription")}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                borderColor: osMode === "EXTERNAL" ? "primary.main" : "divider",
                borderWidth: osMode === "EXTERNAL" ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => setOsMode("EXTERNAL")}>
                <CardContent>
                  <Typography fontWeight={700}>{t("workspace.setup.externalTitle")}</Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {t("workspace.setup.externalDescription")}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Stack>

          {osMode === "BEAS_MANAGED" ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              {t("workspace.setup.beasNoFormHint")}
            </Alert>
          ) : (
            <Stack spacing={2}>
              <AppTextField
                label={t("workspace.setup.openSearchUrl")}
                required
                value={settings.openSearchUrl || ""}
                onChange={(e) => setSettings({ ...settings, openSearchUrl: e.target.value })}
              />
              <AppTextField
                label={t("workspace.setup.openSearchUsername")}
                value={settings.openSearchUsername || ""}
                onChange={(e) =>
                  setSettings({ ...settings, openSearchUsername: e.target.value })
                }
              />
              <AppTextField
                label={
                  settings.openSearchPasswordSet
                    ? t("workspace.setup.openSearchPasswordKeep")
                    : t("workspace.setup.openSearchPassword")
                }
                type="password"
                value={osPassword}
                onChange={(e) => setOsPassword(e.target.value)}
              />
              <AppCheckbox
                label={t("workspace.setup.openSearchSslInsecure")}
                checked={settings.openSearchSslInsecure}
                onChange={(e) =>
                  setSettings({ ...settings, openSearchSslInsecure: e.target.checked })
                }
              />
            </Stack>
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
            <AppButton variant="outlined" onClick={() => setActive(0)}>
              {t("workspace.setup.back")}
            </AppButton>
            <AppButton variant="contained" disabled={saving} onClick={() => nextFromOs().catch(() => {})}>
              {osMode === "BEAS_MANAGED"
                ? t("workspace.setup.useBeas")
                : t("workspace.setup.continue")}
            </AppButton>
            <AppButton variant="outlined" onClick={skipForLater}>
              {t("workspace.setup.configureLater")}
            </AppButton>
          </Box>
        </Box>
      )}

      {active === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {t("workspace.setup.testStatus", {
              llm: String(settings.llmOk),
              os: String(settings.openSearchOk),
            })}
          </Alert>
          {settings.openSearchMode === "BEAS_MANAGED" && settings.openSearchIndexPrefix && (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              {t("workspace.setup.beasPrefixHint", {
                prefix: settings.openSearchIndexPrefix,
              })}
            </Alert>
          )}
          <Typography variant="body2" mb={2}>
            {t("workspace.setup.testHint")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <AppButton variant="outlined" onClick={() => setActive(1)}>
              {t("workspace.setup.back")}
            </AppButton>
            <AppButton variant="contained" disabled={saving} onClick={runTests}>
              {t("workspace.setup.testAndFinish")}
            </AppButton>
            <AppButton variant="outlined" onClick={skipForLater}>
              {t("workspace.setup.configureLater")}
            </AppButton>
          </Box>
        </Box>
      )}
    </AuthPageShell>
  );
}
